import { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { FormFieldConfig } from '../../Hooks/useForm';
import { getGuid } from '../../Utils/stringUtils';
import { BizControlSize } from '../../Types/controlSize';
import { useDebounce } from '../../Hooks/useDebounce';
import './biz-autocomplete.scss';
import { Popper } from '@mui/material';
import { PopperProps } from '@mui/material/Popper/BasePopper.types';

const DEFAULT_MIN_SEARCH_LENGTH = 2;

//make it as wide as the widest option
const CustomPopper = (props: PopperProps) => {
	return (
		<Popper
			{...props}
			style={{ ...props.style, zIndex: 'calc(var(--biz-popup-z-index, 999) + 10)' }}
			modifiers={[
				{
					name: 'preventOverflow',
					options: {
						boundary: 'window',
					},
				},
				{
					name: 'setWidth',
					enabled: true,
					phase: 'beforeWrite',
					fn: ({ state }) => {
						const referenceWidth = state.rects.reference.width;
						const maxOptionWidth = Math.max(
							...Array.from(state.elements.popper.querySelectorAll('li')).map((el) => el.scrollWidth)
						);
						state.styles.popper.width = `${Math.max(referenceWidth, maxOptionWidth)}px`;
					},
				},
			]}
		/>
	);
};

type BizAutoCompleteProps<OptionType> = {
	size?: BizControlSize;
	searchOptionsFn?: (search: string) => Promise<OptionType[]>;
	options?: OptionType[];
	onChange: (option: OptionType | null) => void;
	className?: string;
	value?: OptionType;
	name?: string;
	getOptionLabel?: (option: OptionType) => string;
	minSearchLength?: number;
	componentLabel?: string;
	hideShrunkLabel?: boolean;
	formConfig?: FormFieldConfig;
};

export function BizAutoComplete<OptionType extends { Id: number | string; Name: string }>({
	searchOptionsFn,
	options: provideOptions,
	onChange,
	className,
	value,
	name: pName,
	getOptionLabel,
	minSearchLength,
	componentLabel,
	hideShrunkLabel,
	formConfig,
	size,
}: BizAutoCompleteProps<OptionType>) {
	const [options, setOptions] = useState<OptionType[]>(provideOptions ?? []);
	const [search, setSearch] = useState('');
	const [name] = useState(pName ?? getGuid());

	const isValid = formConfig?.state.fields[name]?.isValid ?? true;

	useEffect(() => {
		if (provideOptions) {
			setOptions(provideOptions);
		}
	}, [provideOptions]);

	const debouncedValue = useDebounce(search);

	useEffect(() => {
		if (searchOptionsFn && search.length >= (minSearchLength ?? DEFAULT_MIN_SEARCH_LENGTH) && search !== value?.Name) {
			searchOptionsFn(search).then((data) => {
				setOptions(data);
			});
		}
	}, [debouncedValue]);

	useEffect(() => {
		if (!formConfig || !formConfig.validators) return;

		const newIsValid = formConfig.state.validateFieldValue(value?.Id.toString(), formConfig.validators);

		console.log(`Field ${name}`, newIsValid);

		if (newIsValid !== isValid) {
			formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
		}
	}, [value, formConfig?.validators]);

	function onChangeHandler(_event: React.SyntheticEvent, selected: OptionType | null) {
		console.log('onChangeHandler', selected);
		onChange(selected);

		if (formConfig) {
			formConfig.state.setDirty();
		}
	}

	if (!searchOptionsFn && !provideOptions) {
		console.log('BizAutoComplete: no searchOptionsFn or options provided');
		return null;
	}

	const getClasses = () => {
		let classes = 'biz-autocomplete';
		if (className) {
			classes += ` ${className}`;
		}
		if (!isValid) {
			classes += ' invalid';
		}
		if (hideShrunkLabel) {
			classes += ' hide-shrunk-label';
		}
		return classes;
	};

	const isOptionEqualToValue = (option: OptionType, value: OptionType) => {
		return option.Id === value.Id;
	};

	return (
		<Autocomplete
			data-biz-size={size}
			className={getClasses()}
			getOptionLabel={getOptionLabel ?? ((option: OptionType) => option.Name)}
			options={options}
			getOptionKey={(option) => option.Id.toString()}
			onChange={(e, option) => onChangeHandler(e, option)}
			onInputChange={(_, value) => setSearch(value)}
			value={value ?? null}
			noOptionsText={search.length > 0 ? 'Nothing found' : 'Type to search...'}
			isOptionEqualToValue={isOptionEqualToValue}
			renderInput={(params) => <TextField {...params} label={componentLabel} />}
			PopperComponent={CustomPopper}
		/>
	);
}
